'use client';

import React from 'react';
import { ExpandLess, ExpandMore } from '@mui/icons-material';
import { CardProps, Collapse, Skeleton, SvgIconOwnProps } from '@mui/material';
import { SkeletonOwnProps } from '@mui/material/Skeleton/Skeleton';

import { useBreakpoint, useTheme } from '@dh/hooks';
import { BodyMedium, HeadlineMedium, Row } from '@dh/styles';

import { IconButton } from '../buttons';
import { CollapsibleTileContainer, CollapsibleTileContent } from './styles';
import { FormattedOutput } from '@dh/components';

interface Props extends CardProps {
    title: string;
    description: string;
    withMarkdown?: boolean;
    image?: React.ReactNode;
    isExpanded: boolean;
    isLoading?: boolean;
    toggleElement: () => void;
    hasSharpCorners?: boolean;
    iconProps?: SvgIconOwnProps;
    titleColor?: string;
}

const CollapsibleTile = ({
    title,
    description,
    image,
    isExpanded,
    isLoading = false,
    toggleElement,
    hasSharpCorners = true,
    withMarkdown = false,
    iconProps,
    titleColor,
    ...cardProps
}: Props) => {
    const breakpoint = useBreakpoint();
    const { colors } = useTheme();

    const arrowIcon = isExpanded ? <ExpandLess {...iconProps} /> : <ExpandMore {...iconProps} />;

    const renderSkeleton = (props: SkeletonOwnProps) => (
        <Skeleton {...props} sx={{ bgcolor: colors.primaryContainerDark }} />
    );
    return (
        <CollapsibleTileContainer
            sharpCorners={hasSharpCorners}
            breakpoint={breakpoint}
            onClick={toggleElement}
            {...cardProps}
            sx={{ ...cardProps.sx, cursor: 'pointer' }}
        >
            <Row onClick={toggleElement} sx={{ cursor: 'pointer' }}>
                {isLoading ? (
                    renderSkeleton({
                        variant: 'text',
                        width: '33%',
                        children: <HeadlineMedium>Headline</HeadlineMedium>
                    })
                ) : (
                    <>
                        <HeadlineMedium sx={{ color: titleColor ?? colors.sysLightSurfaceTint }} flex={1}>
                            {title}
                        </HeadlineMedium>
                        <IconButton icon={arrowIcon} onClick={toggleElement} />
                    </>
                )}
            </Row>
            <Collapse in={isExpanded}>
                <CollapsibleTileContent breakpoint={breakpoint}>
                    {isLoading ? (
                        <>
                            {renderSkeleton({ variant: 'rectangular', width: 200, height: 300 })}
                            {renderSkeleton({ variant: 'rectangular', width: '100%', height: 100 })}
                        </>
                    ) : (
                        <>
                            {image}
                            {withMarkdown === true ? (
                                <FormattedOutput replaceNewLines>{description}</FormattedOutput>
                            ) : (
                                <BodyMedium>{description}</BodyMedium>
                            )}
                        </>
                    )}
                </CollapsibleTileContent>
            </Collapse>
        </CollapsibleTileContainer>
    );
};

export { CollapsibleTile };
